import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import "./PayOrderPage.css";

const BASE_URL = "https://fooapp.mx";
// Strip /graphql suffix since pay-order is a REST endpoint
const API_URL = (process.env.REACT_APP_API_URL || "https://api.fooapp.mx").replace(/\/graphql\/?$/, "");

interface OrderProduct {
  name: string;
  qty: number;
  price: number;
  ingredients_txt?: string;
  comments?: string;
}

interface OrderData {
  id: number;
  status: string;
  payed: boolean;
  total: number;
  subtotal: number;
  shipping: number;
  delivery_type: string;
  products: OrderProduct[];
  restaurant_name: string;
  restaurant_logo: string | null;
  stripe_connect_active: boolean;
}

function formatPrice(amount: number): string {
  return amount.toLocaleString("es-MX", {
    style: "currency",
    currency: "MXN",
  });
}

const PayOrderPage: React.FC = () => {
  const { partnerId, orderId } = useParams<{ partnerId: string; orderId: string }>();
  const [order, setOrder] = useState<OrderData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    if (!partnerId || !orderId) {
      setError("not_found");
      setLoading(false);
      return;
    }

    fetch(`${API_URL}/api/pay-order/${partnerId}/${orderId}`)
      .then((res) => {
        if (res.status === 404) throw new Error("not_found");
        if (!res.ok) throw new Error("error");
        return res.json();
      })
      .then((data: OrderData) => {
        setOrder(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || "error");
        setLoading(false);
      });
  }, [partnerId, orderId]);

  const handlePay = async () => {
    if (!partnerId || !orderId) return;
    setRedirecting(true);
    try {
      const res = await fetch(`${API_URL}/api/pay-order/${partnerId}/${orderId}/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) throw new Error("checkout_error");
      const data = await res.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error("checkout_error");
      }
    } catch {
      setRedirecting(false);
      setError("checkout_error");
    }
  };

  // Already paid
  const renderPaid = () => (
    <div className="pay-body">
      <div className="pay-status-icon success">
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2.5}
            d="M5 13l4 4L19 7"
          />
        </svg>
      </div>
      <h2>Este pedido ya fue pagado</h2>
      <p className="subtitle">
        El pago de este pedido ya fue procesado correctamente.
      </p>
    </div>
  );

  // No Stripe Connect
  const renderNoStripe = () => (
    <div className="pay-body">
      <div className="pay-status-icon warning">
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2.5}
            d="M12 8v4m0 4h.01"
          />
        </svg>
      </div>
      <h2>Pagos no disponibles</h2>
      <p className="subtitle">
        Este restaurante no acepta pagos en linea por el momento.
      </p>
    </div>
  );

  // Not found
  const renderNotFound = () => (
    <div className="pay-body">
      <div className="pay-status-icon error">
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2.5}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </div>
      <h2>Pedido no encontrado</h2>
      <p className="subtitle">
        El enlace de pago no es valido o el pedido no existe.
      </p>
    </div>
  );

  // Checkout error
  const renderCheckoutError = () => (
    <div className="pay-body">
      <div className="pay-status-icon error">
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2.5}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </div>
      <h2>Error al procesar el pago</h2>
      <p className="subtitle">
        Ocurrio un error al iniciar el pago. Intenta de nuevo.
      </p>
      <button className="pay-cta" onClick={() => setError(null)}>
        Reintentar
      </button>
    </div>
  );

  // Order details + pay
  const renderOrder = (order: OrderData) => {
    const logoUrl = order.restaurant_logo
      ? order.restaurant_logo.startsWith("http")
        ? order.restaurant_logo
        : `${BASE_URL}${order.restaurant_logo}`
      : null;

    return (
      <>
        <div className="pay-body">
          <div className="pay-restaurant">
            {logoUrl && (
              <img
                src={logoUrl}
                alt={order.restaurant_name}
                className="pay-restaurant-logo"
              />
            )}
            <h3 className="pay-restaurant-name">{order.restaurant_name}</h3>
            <p className="pay-order-id">Pedido #{order.id}</p>
          </div>

          {/* Products */}
          <div className="pay-products">
            {order.products.map((product, index) => (
              <div key={index} className="pay-product-row">
                <span className="pay-product-name">
                  {product.qty > 1 && (
                    <span className="pay-product-qty">{product.qty}x </span>
                  )}
                  {product.name}
                </span>
                <span className="pay-product-price">
                  {formatPrice(product.price * product.qty)}
                </span>
              </div>
            ))}
          </div>

          {/* Summary */}
          <div className="pay-summary">
            <div className="pay-summary-row">
              <span className="pay-summary-label">Subtotal</span>
              <span className="pay-summary-value">
                {formatPrice(order.subtotal)}
              </span>
            </div>
            {order.shipping > 0 && (
              <div className="pay-summary-row">
                <span className="pay-summary-label">Envio</span>
                <span className="pay-summary-value">
                  {formatPrice(order.shipping)}
                </span>
              </div>
            )}
            <div className="pay-summary-row total">
              <span className="pay-summary-label">Total</span>
              <span className="pay-summary-value">
                {formatPrice(order.total)}
              </span>
            </div>
          </div>

          <button
            className="pay-cta"
            onClick={handlePay}
            disabled={redirecting}
          >
            {redirecting ? (
              <span className="spinner-inline" />
            ) : (
              `Pagar ${formatPrice(order.total)}`
            )}
          </button>
        </div>

        <div className="pay-footer">
          <p>
            El pago sera procesado de forma segura a traves de Stripe.
            <br />
            Si tienes alguna duda, contacta directamente a{" "}
            {order.restaurant_name}.
          </p>
        </div>
      </>
    );
  };

  // Determine what to render
  const renderContent = () => {
    if (loading) {
      return (
        <div className="pay-loading">
          <div className="spinner" />
          <p style={{ color: "#888" }}>Cargando pedido...</p>
        </div>
      );
    }

    if (error === "checkout_error" && order) {
      return renderCheckoutError();
    }

    if (error === "not_found" || (!order && error)) {
      return renderNotFound();
    }

    if (error) {
      return renderNotFound();
    }

    if (!order) {
      return renderNotFound();
    }

    if (order.payed) {
      return renderPaid();
    }

    if (!order.stripe_connect_active) {
      return renderNoStripe();
    }

    return renderOrder(order);
  };

  return (
    <>
      <Helmet>
        <title>
          {order?.restaurant_name
            ? `Pagar pedido - ${order.restaurant_name} | Foo`
            : "Pagar pedido | Foo"}
        </title>
        <meta name="theme-color" content="#292929" />
      </Helmet>

      <div className="pay-container">
        <div className="pay-card">
          <div className="pay-header">
            <img src={`${BASE_URL}/img/logo_svg.svg`} alt="Foo" />
          </div>
          {renderContent()}
        </div>
      </div>
    </>
  );
};

export default PayOrderPage;
